import { Box, Button, Tag, Text } from '@factorialco/deprecated-design-system'
import {
  AtsGenerateJobPostingDraftDescriptionDocument,
  JobPostingDraftDescriptionSubscriptionDocument,
} from 'generated/resources/graphql'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useSubscription } from 'modules/core/hooks/useSubscription'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { GenerateDraftDescriptionMessage } from './GenerateDraftDescriptionMessage'

interface Props {
  onClickGenerate: () => Promise<void>
  loading: boolean
  alreadyGenerated: boolean
  currentDescription: string
}

export const useGenerateDraftDescription = (jobId: string) => {
  const [generateDraftDescription, { loading, called }] = useMutation(
    AtsGenerateJobPostingDraftDescriptionDocument,
    {
      extractorFn: (d) => d.atsMutations.generateDraftAtsJobPosting,
      variables: { id: jobId },
    }
  )
  const { data } = useSubscription(JobPostingDraftDescriptionSubscriptionDocument)

  const draft = data?.atsJobPostingGenerateDraft?.jobPosting
  const draftDescription = draft?.description
  const draftError = draft?.error

  const loadingDraft = loading || (!draftDescription && called && !draftError)

  useEffect(() => {
    if (draftError && !draftDescription) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('ats.job_posting_settings.description.generate_draft.error'),
      })
    }
  }, [draftError, draftDescription])

  const generateDraftDescriptionCallback = useUnsafeCallback(async () => {
    await generateDraftDescription({})
    return undefined
  }, [generateDraftDescription])

  return {
    generateDraftDescription: generateDraftDescriptionCallback,
    generatingDraftDescription: loadingDraft,
    alreadyGeneratedDraftDescription: called && !draftError,
    draftDescription,
  }
}

export const GenerateDraftDescription = ({
  onClickGenerate,
  loading,
  alreadyGenerated,
  currentDescription,
}: Props) => {
  const showGenerateDraftDescription = (!alreadyGenerated || loading) && !currentDescription
  const showGenerateDraftMessage = alreadyGenerated && !loading

  return (
    <>
      {showGenerateDraftMessage && <GenerateDraftDescriptionMessage />}
      {showGenerateDraftDescription && (
        <Box
          background="viridian100"
          paddingY="s16"
          paddingX="s24"
          gap="s8"
          borderRadius={{ all: 'abs008' }}
        >
          <Tag inline icon="ai" leftItem="icon" type="viridian" solid>
            {i18n.t('ats.job_posting_settings.description.generate_draft.factorial_ai')}
          </Tag>
          <Text.Normal>
            {i18n.t('ats.job_posting_settings.description.generate_draft.description')}
          </Text.Normal>

          <Box flexDirection="row" justifyContent="flexEnd">
            <Button loading={loading} onClick={onClickGenerate} iconName="ai">
              {i18n.t('ats.job_posting_settings.description.generate_draft.action')}
            </Button>
          </Box>
        </Box>
      )}
    </>
  )
}
